import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getMergedRangeFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveParamByFacetId,
    modifyParamsWithRange,
    parseQueryParams,
} from '@/utils/queryParams';

type FormData = {
    max: number | string;
    min: number | string;
};

export const PriceRangeFilter = () => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const priceRangeFacet = useAppSelector((state) => getMergedRangeFacet(state, FacetTypes.ESTIMATED_PRICE_ID));
    const { submitted: isLoadingSearchResults } = useAppSelector(getSearchUiState);

    const {
        formState: { errors },
        handleSubmit,
        register,
        watch,
    } = useForm<FormData>({
        defaultValues: {
            max: priceRangeFacet?.range?.max === 0 ? '' : (priceRangeFacet?.range?.max ?? ''),
            min: priceRangeFacet?.range?.min === 0 ? '' : (priceRangeFacet?.range?.min ?? ''),
        },
    });

    const formWatch = watch();

    const onPriceRangeRangeChanged = useCallback(
        ({ max, min }: FormData) => {
            let newParams = modifyParamsWithRange(queryParams, FacetTypes.ESTIMATED_PRICE_ID, Number(min), Number(max));
            if (min === 0 && max === 0) {
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.ESTIMATED_PRICE_ID);
            }

            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                currentOpenMenu: CurrentOpenMenu.AllFilters,
                filterAction: min === 0 && max === 0 ? FilterAction.PriceRangeRemoved : FilterAction.PriceRangeChanged,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [queryParams, navigateWithParams]
    );

    // RESET FORM WHEN INPUTS ARE EMPTY AND CURRENT BID FACET IS NOT EMPTY
    useEffect(() => {
        if (priceRangeFacet?.range?.max !== 0 && formWatch.max === '' && formWatch.min === '') {
            onPriceRangeRangeChanged({ max: 0, min: 0 });
        }
        // callback and facet are excluded from dependencies to prevent infinite loop
        // updating URL triggers server response with new facet data, which would re-run the effect indefinitely.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formWatch.max, formWatch.min]);

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={formatMessage({ id: 'searchPage.facets.priceRange' })}
            open
        >
            <form
                className="grid grid-cols-[1fr_min-content_1fr_min-content] items-start gap-xs"
                onSubmit={handleSubmit(onPriceRangeRangeChanged)}
            >
                <TextInput
                    {...register('min', { required: true })}
                    disabled={isLoadingSearchResults}
                    labelText="Min"
                    placeholder="$1"
                    prefix={formWatch.min ? '$' : ''}
                    small
                    type="number"
                />
                <BodyPrimary className="mt-md">-</BodyPrimary>
                <TextInput
                    {...register('max', {
                        required: true,
                        validate: (value) => Number(value) >= Number(formWatch.min),
                    })}
                    disabled={isLoadingSearchResults}
                    errorText="Please enter a valid range"
                    hasError={Boolean(errors.max)}
                    labelText="Max"
                    placeholder="$100"
                    prefix={formWatch.max ? '$' : ''}
                    small
                    type="number"
                />
                <Button
                    className="mr-[1px] mt-[18px]"
                    loading={isLoadingSearchResults}
                    size="sm"
                    type="submit"
                    variant="outlined"
                >
                    {formatMessage({ id: 'searchPage.facets.applyFilter' })}
                </Button>
            </form>
        </Accordion>
    );
};
